import React, { useEffect, useState } from "react";
import AdminTable from "common/AdminTable";
import axiosInstance from "apis/axiosConfig";

const statusBadgeStyle = {
  background: "#dcfce7",
  color: "#15803d",
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: 700,
  display: "inline-block",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #ced4da",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "14px",
  outline: "none",
};

const modalButtonStyle = {
  border: "none",
  borderRadius: "6px",
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const ModalShell = ({ title, children, onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.45)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "16px",
    }}
  >
    <div
      className="modal-content border-0 shadow"
      style={{ width: "100%", maxWidth: "560px", borderRadius: "12px" }}
    >
      <div className="modal-header bg-light">
        <h5 className="modal-title fw-semibold text-dark mb-0">{title}</h5>
        <button className="close btn text-danger" onClick={onClose} style={{ fontSize: "1.5rem" }}>
          &times;
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </div>
  </div>
);

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/categories");
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openEditModal = (row) => {
    setEditingCategory(row);
    setCategoryName(row.name || "");
  };

  const closeEditModal = () => {
    setEditingCategory(null);
    setCategoryName("");
  };

  const handleUpdate = async () => {
    const trimmedName = categoryName.trim();
    if (!editingCategory?.id || !trimmedName) return;

    try {
      await axiosInstance.patch(`/categories/${editingCategory.id}`, {
        name: trimmedName,
        status: editingCategory.status || "Active",
      });
      await fetchCategories();
      closeEditModal();
    } catch (error) {
      console.error("Failed to update category:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await axiosInstance.delete(`/categories/${id}`);
      await fetchCategories();
    } catch (error) {
      console.error("Failed to delete category:", error);
    }
  };

  const tableHeaders = [
    { Header: "#", accessor: "id" },
    { Header: "Category Name", accessor: "name" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => (
        <span style={statusBadgeStyle}>{row.original.status || "Active"}</span>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center flex-wrap" style={{ gap: "8px" }}>
          <button className="btn btn-sm btn-info text-white" onClick={() => openEditModal(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Category List</h4>
        </div>

        <div className="mx-4 mb-2 text-muted">{categories.length} records</div>

        {loading ? (
          <div className="mx-4 text-muted">Loading categories...</div>
        ) : categories.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={categories} />
        ) : (
          <div className="mx-4 text-muted">No category records found.</div>
        )}
      </div>

      {editingCategory && (
        <ModalShell title="Edit Category" onClose={closeEditModal}>
          <div className="form-group mb-3">
            <label htmlFor="category-name" className="admin-label">
              Category Name
            </label>
            <input
              id="category-name"
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div className="d-flex justify-content-end" style={{ gap: "10px" }}>
            <button
              onClick={closeEditModal}
              style={{ ...modalButtonStyle, background: "#e9ecef", color: "#495057" }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              style={{ ...modalButtonStyle, background: "#198754", color: "#fff" }}
            >
              Update
            </button>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default CategoryList;
